import i18n from "i18next";

/**
 * Open a served file (a signed storage URL) in a NEW tab — the app must never navigate away
 * (UAT round 3).
 *
 * Why not a plain anchor: our file URLs are cross-origin presigned bucket URLs, and the anchor
 * `download` attribute is ignored cross-origin — a same-tab anchor click *navigated the SPA to the
 * file*. Why not `window.open` at completion: the export flows poll the worker for seconds before
 * the URL exists, and by then the click's transient activation has expired, so the popup blocker
 * eats the tab. So the tab is opened synchronously inside the click ({@link openFileTab}), shows a
 * localized "preparing" note, and is pointed at the file once ready (`deliver`) or closed again on
 * failure (`fail`).
 */
export interface FileTab {
  /** Navigate the pre-opened tab to the ready file URL. */
  deliver(url: string): void;
  /** The file never materialized — close the pre-opened tab. */
  fail(): void;
}

/**
 * Open the placeholder tab. Call this synchronously in the click handler (inside the user
 * activation), before any await. Returns null when a popup blocker refused even that — callers
 * then fall back to {@link anchorNewTab} at delivery time (best effort).
 */
export function openFileTab(): FileTab | null {
  const tab = window.open("about:blank", "_blank");
  if (!tab) return null;
  // Sever the reverse handle (the tab must not be able to script the app); our forward handle
  // stays usable for navigating it.
  tab.opener = null;
  try {
    const note = i18n.t("common.preparingFile");
    tab.document.title = note;
    tab.document.body.textContent = note;
  } catch {
    /* cosmetic only */
  }
  return {
    deliver(url: string) {
      try {
        tab.location.replace(url);
      } catch {
        anchorNewTab(url);
      }
    },
    fail() {
      try {
        tab.close();
      } catch {
        /* already gone */
      }
    },
  };
}

/** Fallback: a transient new-tab anchor (may be popup-blocked outside a user activation). */
export function anchorNewTab(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
